<template>
  <scoped>
    <VerticalNavigator>
      <Breadcrumb
        :links="[{
          title: 'RutSolve',
          routeName: 'index'
        }, {
          title: 'Sale Orders',
          routeName: 'sale_order_overview'
        }]"
        title="Create"
      />
      <h1>
        Create Sale Order
      </h1>
      <SaleOrderEditor v-model:value="order" />
      <h2>
        Customer
      </h2>
      <Button
        title="Customer"
        @click="selectingCustomer = true"
      />
      <ContentModal v-if="selectingCustomer">
        <CustomerSelector
          v-model:value="order.customer"
          @select="selectCustomer()"
        />
      </ContentModal>
      <h2>
        Order Lines
      </h2>
      <OrderLineList
        v-if="order.orderLines"
        v-model:items="order.orderLines.records"
        v-model:selectedIndex="selectedOrderLineIndex"
        @select="selectedOrderLine = order.orderLines.records[selectedOrderLineIndex]"
      />
      <ContentModal v-if="selectedOrderLine">
        <Button
          title="Deliverable"
          @click="selectingDeliverable = true"
        />
        <ContentModal v-if="selectingDeliverable">
          <DeliverableSelector
            v-model:value="selectedOrderLine.deliverable"
            @select="selectDeliverable()"
          />
        </ContentModal>
        <OrderLineEditor v-model:value="selectedOrderLine" />
        <br>
        <Button
          title="Apply"
          @click="selectedOrderLine = null"
        />
      </ContentModal>
      <h2>
        Contact Details
      </h2>
      <ContactDetailsList
        v-if="order.contactDetails"
        v-model:items="order.contactDetails.records"
        v-model:selectedIndex="selectedContactDetailsIndex"
        @select="selectedContactDetails = order.contactDetails.records[selectedContactDetailsIndex]"
      />
      <ContentModal v-if="selectedContactDetails">
        <ContactDetailsEditor v-model:value="selectedContactDetails" />
        <br>
        <Button
          title="Apply"
          @click="selectedContactDetails = null"
        />
      </ContentModal>
      <h2>
        Files
      </h2>
      <FileList
        v-if="order && order.files"
        v-model:items="order.files.records"
        v-model:selectedIndex="selectedFileIndex"
        @select="selectedFile = order.files.records[selectedFileIndex]"
      />
      <ContentModal v-if="selectedFile">
        <FileEditor v-model:value="selectedFile" />
        <br>
        <Button
          title="Apply"
          @click="selectedFile = null"
        />
      </ContentModal>
      <Button
        title="Cancel"
        @click="ordersRedirect()"
      />
      <Button
        title="Save"
        @click="save()"
      />
    </VerticalNavigator>
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import VerticalNavigator from "@/layouts/vertical_navigator.vue"
  import Breadcrumb from "@/components/breadcrumb.vue"
  import SaleOrderEditor from "@/components/editors/sale_order.vue"
  import {Order as OrderClient} from "@/endpoints/rutsolve/gql/resolvers/order"
  import {ref} from "vue"
  import {
    Order,
    ContactDetails,
    OrderLine, File
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import Button from "@/components/button.vue"
  import router from "@/router"
  import ErrorModal from "@/components/modals/error.vue"
  import {OrderLine as OrderLineClient} from "@/endpoints/rutsolve/gql/resolvers/order_line"
  import {ContactDetails as ContactDetailsClient} from "@/endpoints/rutsolve/gql/resolvers/contact_details"
  import CustomerSelector from "@/components/selectors/customer.vue"
  import DeliverableSelector from "@/components/selectors/deliverable.vue"
  import OrderLineEditor from "@/components/editors/order_line.vue"
  import ContentModal from "@/components/modals/content.vue"
  import OrderLineList from "@/components/lists/order_line.vue"
  import ContactDetailsEditor from "@/components/editors/contact_details.vue"
  import ContactDetailsList from "@/components/lists/contact_details.vue"
  import {File as FileClient} from "@/endpoints/rutsolve/gql/resolvers/file"
  import FileList from "@/components/lists/file.vue"
  import FileEditor from "@/components/editors/file.vue"

  const order = ref<Order>({
    contactDetails: {
      records: [],
    },
    currency: "Euro",
    files: {
      records: [],
    },
    orderLines: {
      records: [],
    },
    shiftedVAT: true,
  })

  const error = ref<Error>(null)
  const fileClient = new FileClient()
  const orderClient = new OrderClient()
  const orderLineClient = new OrderLineClient()
  const contactDetailsClient = new ContactDetailsClient()
  const selectedContactDetails = ref<ContactDetails>(null)
  const selectedContactDetailsIndex = ref<number>(null)
  const selectedFile = ref<File>(null)
  const selectedFileIndex = ref<number>(null)
  const selectedOrderLine = ref<OrderLine>(null)
  const selectedOrderLineIndex = ref<number>(null)
  const selectingCustomer = ref<boolean>(null)
  const selectingDeliverable = ref<boolean>(null)

  async function save() {
    try {
      const response = await orderClient.create(order.value)
      const orderID = response.id
      for (const contactDetails of order.value.contactDetails.records) {
        if (contactDetails.deletedAt) {
          continue
        }
        contactDetails.relationID = orderID
        contactDetails.relationType = "ORDER"
        await contactDetailsClient.create(contactDetails)
      }
      for (const file of order.value.files.records) {
        if (file.deletedAt) {
          continue
        }
        file.relationID = orderID
        file.relationType = "ORDER"
        await fileClient.create(file)
      }
      for (const orderLine of order.value.orderLines.records) {
        if (orderLine.deletedAt) {
          continue
        }
        orderLine.orderID = orderID
        await orderLineClient.create(orderLine)
      }
      ordersRedirect()
    } catch(e) {
      error.value = e
    }
  }

  function ordersRedirect() {
    router.push({
      name: "sale_order_overview"
    })
  }

  function selectCustomer() {
    order.value.relationID = order.value.customer.id
    order.value.relationType = "CUSTOMER"
    if (order.value.customer.contactDetails && order.value.customer.contactDetails.records) {
      order.value.contactDetails.records = []
      let hasBilling = false
      let hasDelivery = false
      for (const contactDetails of order.value.customer.contactDetails.records) {
        if (!hasBilling && contactDetails.type == "BILLING") {
          order.value.contactDetails.records.push(contactDetails)
          hasBilling = true
        }
        if (!hasDelivery && contactDetails.type == "DELIVERY") {
          order.value.contactDetails.records.push(contactDetails)
          hasDelivery = true
        }
      }
    }
    selectingCustomer.value = false
  }

  function selectDeliverable() {
    selectedOrderLine.value.amount = 1
    selectedOrderLine.value.deliverableID = selectedOrderLine.value.deliverable.id
    selectedOrderLine.value.description = selectedOrderLine.value.deliverable.name
    selectedOrderLine.value.unit = selectedOrderLine.value.deliverable.unit
    selectedOrderLine.value.unitPrice = selectedOrderLine.value.deliverable.unitPrice
    selectedOrderLine.value.vatRate = selectedOrderLine.value.deliverable.vatRate
    selectingDeliverable.value = false
  }
</script>
